// Shared compatible-combinations filter pieces (AHA-6681 CS-1): the pure
// types, chip styling, and the three faceted filter controls used by both
// the Compatible Combinations Browser modal and the Choose Systems ducted
// filter section. Both surfaces track the same pnet API contract, so these
// live in one place. Render orchestration (which controls to show for which
// facet) deliberately stays with each surface.
import clsx from 'clsx';
import InfoTooltip from 'components/layout/info-tooltip';
import { useEffect, useState } from 'react';

// Active filter selections sent to the backend (mirrors the API contract).
export type CombinationFilter =
  | { field: string; kind: 'range'; min?: number; max?: number }
  | { field: string; kind: 'enum'; values: string[] }
  | { field: string; kind: 'boolean'; value: boolean };

// Faceted filter option descriptors returned by the backend, used to render the
// filter controls. Counts/min/max already reflect the other active filters.
export type CombinationFilterOption =
  | {
      field: string;
      label: string;
      group: string;
      kind: 'range';
      min: number | null;
      max: number | null;
    }
  | {
      field: string;
      label: string;
      group: string;
      kind: 'enum';
      values: { value: string; count: number }[];
    }
  | {
      field: string;
      label: string;
      group: string;
      kind: 'boolean';
      trueCount: number;
      falseCount: number;
    };

export function formatFilterNumber(value: number): string {
  return Number.isInteger(value)
    ? String(value)
    : String(Math.round(value * 100) / 100);
}

// Group faceted options into labeled sections, preserving the backend's
// (registry) order of first appearance per group.
export function groupCombinationFilterOptions(
  filterOptions: CombinationFilterOption[]
): { group: string; options: CombinationFilterOption[] }[] {
  const groups: { group: string; options: CombinationFilterOption[] }[] = [];
  const indexByGroup = new Map<string, number>();
  for (const option of filterOptions) {
    const key = option.group ?? 'Filters';
    let idx = indexByGroup.get(key);
    if (idx === undefined) {
      idx = groups.length;
      indexByGroup.set(key, idx);
      groups.push({ group: key, options: [] });
    }
    groups[idx].options.push(option);
  }
  return groups;
}

export const CHIP_BASE = 'rounded px-2 py-1 text-xs font-medium';
export const CHIP_ACTIVE = 'bg-blue-600 text-white';
export const CHIP_INACTIVE = 'bg-gray-100 text-gray-700 hover:bg-gray-200';

export function RangeFilterControl({
  option,
  min,
  max,
  onCommit,
  disabled,
  info,
  showLabel = true
}: {
  option: Extract<CombinationFilterOption, { kind: 'range' }>;
  min: number | undefined;
  max: number | undefined;
  onCommit: (min: number | undefined, max: number | undefined) => void;
  // Structure-requirements mode (AHA-6681): the inputs display the band
  // values (synced from min/max) but reject manual edits.
  disabled?: boolean;
  // Admin-managed info tooltip content (AHA-6681); an info icon next to the
  // field label — used inside multi-field groups (Performance, Capacity)
  // where one group-header blurb cannot explain each metric.
  info?: string;
  showLabel?: boolean;
}) {
  const [localMin, setLocalMin] = useState(min?.toString() ?? '');
  const [localMax, setLocalMax] = useState(max?.toString() ?? '');

  // Keep inputs in sync when the active filter changes externally (e.g. cleared).
  useEffect(() => setLocalMin(min?.toString() ?? ''), [min]);
  useEffect(() => setLocalMax(max?.toString() ?? ''), [max]);

  const commit = () => {
    const parsedMin = localMin.trim() === '' ? undefined : Number(localMin);
    const parsedMax = localMax.trim() === '' ? undefined : Number(localMax);
    onCommit(
      Number.isFinite(parsedMin as number) ? (parsedMin as number) : undefined,
      Number.isFinite(parsedMax as number) ? (parsedMax as number) : undefined
    );
  };

  const step = option.field === 'tonnage' ? 0.5 : undefined;

  return (
    <div className='flex flex-col gap-1'>
      {(showLabel || info) && (
        <span
          className={clsx(
            'text-xs font-medium text-gray-600',
            // Flex only when the icon renders — surfaces that pass no info
            // (the CCB modal) keep their exact pre-feature DOM.
            info && 'flex items-center gap-1'
          )}
        >
          {showLabel ? option.label : null}
          {info && <InfoTooltip>{info}</InfoTooltip>}
        </span>
      )}
      <div className='flex items-center gap-1'>
        <input
          type='number'
          value={localMin}
          disabled={disabled}
          step={step}
          placeholder={
            option.min != null ? formatFilterNumber(option.min) : 'min'
          }
          onChange={(e) => setLocalMin(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
          }}
          className='w-20 rounded border border-gray-300 px-2 py-1 text-xs disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500'
        />
        <span className='text-gray-400'>–</span>
        <input
          type='number'
          value={localMax}
          disabled={disabled}
          step={step}
          placeholder={
            option.max != null ? formatFilterNumber(option.max) : 'max'
          }
          onChange={(e) => setLocalMax(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
          }}
          className='w-20 rounded border border-gray-300 px-2 py-1 text-xs disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500'
        />
      </div>
    </div>
  );
}

export function EnumFilterControl({
  option,
  selected,
  onToggle,
  showLabel = true
}: {
  option: Extract<CombinationFilterOption, { kind: 'enum' }>;
  selected: string[];
  onToggle: (value: string) => void;
  showLabel?: boolean;
}) {
  return (
    <div className='flex flex-col gap-1'>
      {showLabel && (
        <span className='text-xs font-medium text-gray-600'>
          {option.label}
        </span>
      )}
      <div className='flex flex-wrap gap-1'>
        {option.values.map((opt) => (
          <button
            type='button'
            key={opt.value}
            className={`${CHIP_BASE} ${
              selected.includes(opt.value) ? CHIP_ACTIVE : CHIP_INACTIVE
            }`}
            onClick={() => onToggle(opt.value)}
          >
            {opt.value} ({opt.count})
          </button>
        ))}
      </div>
    </div>
  );
}

export function BooleanFilterControl({
  option,
  value,
  onChange,
  showLabel = true
}: {
  option: Extract<CombinationFilterOption, { kind: 'boolean' }>;
  value: boolean | undefined;
  onChange: (value: boolean | undefined) => void;
  showLabel?: boolean;
}) {
  return (
    <div className='flex flex-col gap-1'>
      {showLabel && (
        <span className='text-xs font-medium text-gray-600'>
          {option.label}
        </span>
      )}
      <div className='flex gap-1'>
        <button
          type='button'
          className={`${CHIP_BASE} ${
            value === undefined ? CHIP_ACTIVE : CHIP_INACTIVE
          }`}
          onClick={() => onChange(undefined)}
        >
          All
        </button>
        <button
          type='button'
          className={`${CHIP_BASE} ${
            value === true ? CHIP_ACTIVE : CHIP_INACTIVE
          }`}
          onClick={() => onChange(true)}
        >
          Yes ({option.trueCount})
        </button>
        <button
          type='button'
          className={`${CHIP_BASE} ${
            value === false ? CHIP_ACTIVE : CHIP_INACTIVE
          }`}
          onClick={() => onChange(false)}
        >
          No ({option.falseCount})
        </button>
      </div>
    </div>
  );
}
